import { useState, type FormEvent } from 'react';

import { api, errorMessage } from '../../lib/api';
import { fmtRelative } from '../../lib/format';
import type { Detail } from '../../lib/types';

export const splitPatterns = (text: string): string[] =>
  text
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);

export const AccessEditor = ({ proposalId, access, onChange, notify }: { proposalId: string; access: Detail['access']; onChange: () => void; notify: (t: string) => void }) => {
  const [text, setText] = useState('');
  const [invite, setInvite] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const add = async (e: FormEvent) => {
    e.preventDefault();
    const patterns = splitPatterns(text);
    if (!patterns.length) return;
    setBusy(true);
    setError('');
    try {
      const r = await api<{ added: string[]; invited: string[] }>(`/api/admin/proposals/${proposalId}/access`, { body: { patterns, invite } });
      setText('');
      notify(r.invited.length ? `Acesso liberado. Convite enviado para ${r.invited.length === 1 ? r.invited[0] : `${r.invited.length} pessoas`}.` : 'Acesso liberado.');
      onChange();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const remove = async (id: string, pattern: string) => {
    if (!window.confirm(`Remover o acesso de ${pattern}?`)) return;
    await api(`/api/admin/proposals/${proposalId}/access/${id}`, { method: 'DELETE' });
    onChange();
  };
  const resend = async (id: string, pattern: string) => {
    try {
      await api(`/api/admin/proposals/${proposalId}/access/${id}/invite`, { body: {} });
      notify(`Convite reenviado para ${pattern}.`);
      onChange();
    } catch (err) {
      notify(errorMessage(err));
    }
  };

  return (
    <div className="access">
      {access.length === 0 ? (
        <p className="empty">Ninguém tem acesso ainda. Libere um e-mail ou um domínio inteiro.</p>
      ) : (
        <ul className="access-list">
          {access.map((a) => (
            <li key={a.id}>
              <div>
                <strong>{a.pattern.startsWith('@') ? `Todo ${a.pattern}` : a.pattern}</strong>
                <span>
                  {a.opens ? `${a.opens} ${a.opens === 1 ? 'abertura' : 'aberturas'}` : 'ainda não abriu'}
                  {a.invitedAt ? ` · convite ${fmtRelative(a.invitedAt)}` : ''}
                </span>
              </div>
              <div className="access-act">
                {!a.pattern.startsWith('@') && (
                  <button type="button" className="btn-link" onClick={() => resend(a.id, a.pattern)}>
                    {a.invitedAt ? 'Reenviar' : 'Convidar'}
                  </button>
                )}
                <button type="button" className="icon-btn" aria-label={`Remover ${a.pattern}`} onClick={() => remove(a.id, a.pattern)}>
                  <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
                    <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <form className="access-add" onSubmit={add}>
        <input
          className="input"
          placeholder="ana@cliente.com.br ou @cliente.com.br"
          aria-label="E-mails ou domínios para liberar"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <button className="btn btn-sm" type="submit" disabled={busy || !text.trim()}>
          Liberar
        </button>
        <label className="check" style={{ gridColumn: '1 / -1' }}>
          <input type="checkbox" checked={invite} onChange={(e) => setInvite(e.target.checked)} />
          <span>Enviar convite por e-mail para os endereços liberados</span>
        </label>
        {error && <p className="error" style={{ gridColumn: '1 / -1' }}>{error}</p>}
      </form>
    </div>
  );
};
